import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, CheckCircle, Eye } from "lucide-react";
import { motion } from "framer-motion";

const ChatPage = () => {
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState("");
  const [typing, setTyping] = useState(false);
  const chatEndRef = useRef(null);

  const initialChats = [
    {
      id: "cust1",
      name: "Vishal Chaubey",
      role: "customer",
      status: "Pending",
      chat: [
        { from: "customer", text: "Hello, my product is damaged.", time: "13:00" },
        { from: "admin", text: "Sorry! We will replace it.", time: "13:02" },
      ],
      unread: 0,
    },
    {
      id: "cust2",
      name: "Subh",
      role: "customer",
      status: "Resolved",
      chat: [
        { from: "customer", text: "I got the wrong size.", time: "14:00" },
        { from: "admin", text: "You can return within 7 days.", time: "14:05" },
      ],
      unread: 0,
    },
    {
      id: "vendor1",
      name: "Vendor 1",
      role: "vendor",
      status: "Active",
      chat: [],
      unread: 0,
    },
    {
      id: "vendor2",
      name: "Vendor 2",
      role: "vendor",
      status: "Active",
      chat: [],
      unread: 0,
    },
  ];

  const [chats, setChats] = useState(initialChats);

  // Scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedChat, message, typing]);

  const handleSend = () => {
    if (!message.trim() || !selectedChat) return;

    const now = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    // Add admin message
    setChats((prev) =>
      prev.map((c) =>
        c.id === selectedChat.id
          ? { ...c, chat: [...c.chat, { from: "admin", text: message, time: now }] }
          : c
      )
    );
    setMessage("");

    // Auto-reply simulation
    setTyping(true);
    setTimeout(() => {
      const replyText =
        selectedChat.role === "customer"
          ? "Thanks! We will contact you if needed."
          : "Hello Admin, got your message.";

      setChats((prev) =>
        prev.map((c) => {
          if (c.id === selectedChat.id) {
            return {
              ...c,
              chat: [
                ...c.chat,
                { from: c.role, text: replyText, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
              ],
            };
          } else {
            // Increment unread for other chats
            return { ...c, unread: c.unread + 1 };
          }
        })
      );
      setTyping(false);
    }, 1200);
  };

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    setChats((prev) => prev.map((c) => (c.id === chat.id ? { ...c, unread: 0 } : c)));
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r overflow-y-auto">
        <h1 className="text-2xl font-bold p-4 flex items-center gap-2">
          <MessageCircle size={24} /> Chats
        </h1>
        {chats.map((c) => (
          <div
            key={c.id}
            onClick={() => handleSelectChat(c)}
            className={`cursor-pointer p-4 border-b hover:bg-gray-100 transition-colors ${
              selectedChat?.id === c.id ? "bg-gray-200" : ""
            } flex justify-between items-center`}
          >
            <div>
              <h2 className="font-medium">{c.name}</h2>
              <span
                className={`text-xs font-semibold ${
                  c.role === "customer"
                    ? c.status === "Resolved"
                      ? "text-green-700"
                      : "text-yellow-700"
                    : "text-blue-700"
                }`}
              >
                {c.role === "customer" ? c.status : "Vendor"}
              </span>
            </div>
            {c.unread > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {c.unread}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Chat Window */}
      <div className="flex-1 flex flex-col relative">
        {selectedChat ? (
          <>
            <div className="border-b pb-3 mb-3 flex justify-between items-center px-4">
              <h2 className="text-xl font-bold">{selectedChat.name}</h2>
              <div className="flex gap-2">
                <button className="bg-blue-600 text-white px-3 py-1 rounded-lg flex items-center gap-1">
                  <Eye size={16} /> View
                </button>
                {selectedChat.role === "customer" &&
                  selectedChat.status !== "Resolved" && (
                    <button className="bg-green-600 text-white px-3 py-1 rounded-lg flex items-center gap-1">
                      <CheckCircle size={16} /> Resolve
                    </button>
                  )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 flex flex-col space-y-3">
              {selectedChat.chat.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: msg.from === "admin" ? 50 : -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`max-w-xs p-3 rounded-lg ${
                    msg.from === "admin"
                      ? "bg-blue-600 text-white self-end rounded-br-none"
                      : "bg-gray-200 text-gray-900 self-start rounded-bl-none"
                  }`}
                >
                  <p>{msg.text}</p>
                  <p className="text-[10px] opacity-70 mt-1 text-right">{msg.time}</p>
                </motion.div>
              ))}
              {typing && <div className="text-gray-500 italic text-sm self-start">Typing...</div>}
              <div ref={chatEndRef} />
            </div>

            <div className="p-4 border-t flex gap-2 bg-gray-100">
              <input
                type="text"
                placeholder="Type a message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                className="flex-1 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button onClick={handleSend} className="bg-blue-600 text-white px-4 py-3 rounded-lg">
                Send
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex justify-center items-center text-gray-400">
            Select a chat to start conversation
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
